import { useState, useEffect, useCallback } from 'react'
import { CategoriesService } from '../services/api/categoriesService'
import { ProductCategory } from '../types/category'
import { logger } from '../utils/logger'

interface UseCategoriesReturn {
  categories: ProductCategory[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// Global cache for categories
let categoriesCache: ProductCategory[] | null = null
let cachePromise: Promise<ProductCategory[]> | null = null

export const useCategories = (): UseCategoriesReturn => {
  const [categories, setCategories] = useState<ProductCategory[]>(categoriesCache || [])
  const [isLoading, setIsLoading] = useState(!categoriesCache)
  const [error, setError] = useState<string | null>(null)

  const fetchCategories = useCallback(async (): Promise<ProductCategory[]> => {
    // If we already have a pending request, return that promise
    if (cachePromise) {
      return cachePromise
    }

    // If we have cached data, return it
    if (categoriesCache) {
      return categoriesCache
    }

    // Create new request
    cachePromise = CategoriesService.getCategories()

    try {
      const data = await cachePromise
      categoriesCache = data
      setCategories(data)
      setError(null)
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch categories'
      logger.error('Error fetching categories:', err)
      setError(errorMessage)
      throw err
    } finally {
      cachePromise = null
      setIsLoading(false)
    }
  }, [])

  const refetch = useCallback(async () => {
    // Clear cache to force refresh
    categoriesCache = null
    cachePromise = null
    setIsLoading(true)
    await fetchCategories()
  }, [fetchCategories])

  useEffect(() => {
    if (!categoriesCache && !cachePromise) {
      fetchCategories()
    } else if (categoriesCache) {
      // Data already cached
      setCategories(categoriesCache)
      setIsLoading(false)
    }
  }, [fetchCategories])

  return {
    categories,
    isLoading,
    error,
    refetch
  }
}